<script setup lang="ts">
import type { MenuGroupItem } from "~/components/mail/EditorMenu.vue";
import type { FieldDefinition } from "~/models/Automation";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { getMarkRange } from "@tiptap/core";

const props = defineProps<{
    editor: any;
    show?: boolean;
    fields: FieldDefinition[];
    item: MenuGroupItem;
}>();

const open = ref<boolean>(props.show);
const url = ref<string>();
const query = ref<string>();
const emit = defineEmits(['action']);

const fields = computed(() =>
    props.fields
        .filter((field) => field.sub_type === "url")
        .map((field) => ({
            label: field.actual_label ?? field.label,
            value: makeVariable(field.key),
        })),
);

const options = computed(() => {
    if (
        !fields.value.find(
            (fld) => fld.value === (query.value ? query.value : url.value),
        ) &&
        (url.value || query.value)
    ) {
        return [
            {
                label: query.value ? query.value : url.value,
                value: query.value ? query.value : url.value,
            },
            ...fields.value,
        ];
    }

    return fields.value;
});

const link = computed(() => props.editor.getAttributes("link")?.href ?? "");

watch(link, (to) => {
    query.value = "";
    url.value = to;
});

function setLink() {
    const { $from, $anchor, empty } = props.editor.state.selection;

    if (empty) {
        const mark = $anchor.marks().find((mark: any) => {
            return mark.type.name === "link";
        });

        const range = getMarkRange($from, mark.type, mark.attrs);
        const pos = $anchor.pos;

        if (range && range.from !== range.to) {
            props.editor
                .chain()
                .focus()
                .setTextSelection(range)
                .setLink({
                    href: url.value,
                })
                .setTextSelection(pos)
                .run();
            emit('action');
        }
    } else {
        props.editor
            .chain()
            .focus()
            .setLink({
                href: url.value,
            })
            .run();
        emit('action');
    }
}

function unsetLink() {
    props.editor.chain().focus().unsetLink().run();
    emit('action');
}

function action(action: () => void) {
    action();
    emit('action');
}
</script>

<template>
    <div v-if="show" class="p-2 min-w-max bg-white rounded-md shadow-md flex flex-col gap-2">
        <div class="flex gap-2">
            <UInputMenu v-model="url" :ui="{ icon: { leading: { pointer: '' } } }" option-attribute="label"
                value-attribute="value" class="h-9 w-80" :options="options" v-model:query="query" size="md">
                <template #leading>
                    <FontAwesomeIcon fixed-width :icon="item.icon" />
                </template>
            </UInputMenu>
            <UButton @click.prevent.stop="setLink" class="h-9">
                {{ link ? "Update Link" : "Set Link" }}
            </UButton>
            <UButton v-if="link" @click.prevent.stop="unsetLink" color="gray" variant="soft" square
                class="w-9 h-9 justify-center">
                <FontAwesomeIcon :icon="faTimes" />
            </UButton>
        </div>
    </div>
    <UPopover v-else :popper="{ placement: 'top' }" v-model:open="open" :ui="{
        background: '!overflow-visible',
        base: 'focus:outline-none',
    }">
        <template #panel>
            <div class="p-2 flex flex-col gap-2">
                <div class="flex gap-2">
                    <UInputMenu v-model="url" :ui="{ icon: { leading: { pointer: '' } } }" option-attribute="label"
                        value-attribute="value" class="h-9 w-80" :options="options" v-model:query="query" size="md">
                        <template #leading>
                            <FontAwesomeIcon fixed-width :icon="item.icon" />
                        </template>
                    </UInputMenu>
                    <UButton @click.prevent.stop="
                        action(() => editor.chain().focus().setLink({ href: url }).run())
                        " class="h-9">
                        {{ link ? "Update Link" : "Set Link" }}
                    </UButton>
                    <UButton v-if="link" @click.prevent.stop="action(() => editor.chain().focus().unsetLink().run())"
                        color="gray" variant="soft" square class="w-9 h-9 justify-center">
                        <FontAwesomeIcon :icon="faTimes" />
                    </UButton>
                </div>
            </div>
        </template>
        <MailEditorMenuButton :item="item" />
    </UPopover>
</template>

<style scoped></style>
